"use client";

import { cn } from "cn";
import { SelectNative } from "@/components/ui/select-native";
import { INTERNAL_LINKS } from "@/lib/helpers/urls";
import { CheckIcon } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";

export function LegalSidebarNav({
  docs,
}: {
  docs: readonly { title: string; href: string }[];
}) {
  const pathname = usePathname();
  const [copiedHref, setCopiedHref] = useState<string | null>(null);

  function copyLink(href: string) {
    const url = `${window.location.origin}${href}`;
    navigator.clipboard.writeText(url);
    setCopiedHref(href);
    setTimeout(() => setCopiedHref(null), 2000);
  }

  return (
    <ul className="flex flex-col gap-1">
      {docs.map((doc) => {
        const isActive = pathname === doc.href;
        const wasCopied = copiedHref === doc.href;
        return (
          <li key={doc.href} className="group py-1.5 flex items-baseline">
            <Link
              href={doc.href}
              className={cn(
                "md:text-base font-serif font-medium transition-colors hover:underline",
                isActive
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-700",
              )}
            >
              {doc.title}
            </Link>
            <button
              type="button"
              onClick={() => copyLink(doc.href)}
              aria-label={`Copy link to ${doc.title}`}
              className={cn(
                "inline-flex items-center pl-1.5 min-w-6 text-gray-500 hover:text-gray-950",
                wasCopied
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100 transition-opacity",
              )}
            >
              <span className="relative inline-block w-4">
                {wasCopied ? (
                  <CheckIcon className="size-4 -mb-0.5" />
                ) : (
                  <span>#</span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function LegalBreadcrumbNav({
  docs,
}: {
  docs: readonly { title: string; href: string }[];
}) {
  const router = useRouter();
  const pathname = usePathname();
  const current = docs.find((doc) => doc.href === pathname);

  return (
    <SelectNative
      key={pathname}
      size="sm"
      defaultValue={current?.href ?? ""}
      onChange={(e) => {
        if (e.target.value) router.push(e.target.value);
      }}
      aria-label="Legal document"
      className="h-auto text-sm sm:text-base font-normal border text-gray-500 rounded-xl pl-2 pr-7 py-1 shadow-none hover:bg-transparent"
    >
      {!current && (
        <option value="" disabled>
          Legal Documents
        </option>
      )}
      {docs.map((doc) => (
        <option key={doc.href} value={doc.href}>
          {doc.title}
        </option>
      ))}
    </SelectNative>
  );
}

export function LegalBreadcrumbLink() {
  return (
    <Link
      href={INTERNAL_LINKS.LEGAL.HOME}
      className="hidden md:inline-flex text-sm sm:text-base border text-gray-500 rounded-xl px-2 py-1 hover:text-gray-700 whitespace-nowrap"
    >
      Legal Documents
    </Link>
  );
}
